import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface LightweightCarouselProps {
  children: React.ReactNode[];
  className?: string;
  arrowClassName?: string;
}

export const LightweightCarousel = ({ 
  children, 
  className,
  arrowClassName 
}: LightweightCarouselProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(true);

  const checkScrollability = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollPrev(scrollLeft > 10);
    setCanScrollNext(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollability();
    container.addEventListener('scroll', checkScrollability, { passive: true });
    
    return () => container.removeEventListener('scroll', checkScrollability);
  }, [checkScrollability]);

  const scroll = (direction: 'prev' | 'next') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth;
    const targetScroll = direction === 'next' 
      ? container.scrollLeft + scrollAmount
      : container.scrollLeft - scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  return (
    <div className={cn("relative", className)}>
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {React.Children.map(children, (child, index) => (
          <div 
            key={index}
            className="snap-center flex-shrink-0 w-full"
          >
            {child}
          </div>
        ))}
      </div>
      
      {/* Navigation Arrows */}
      <Button
        variant="outline"
        size="icon"
        className={cn(
          "absolute top-[35%] -translate-y-1/2 h-12 w-12 rounded-full",
          "-left-2 md:-left-8 lg:-left-14",
          "bg-primary hover:bg-primary/80 border-0 shadow-lg text-white",
          !canScrollPrev && "opacity-50 cursor-not-allowed",
          arrowClassName
        )}
        onClick={() => scroll('prev')}
        disabled={!canScrollPrev}
        aria-label="Previous slide"
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      
      <Button
        variant="outline"
        size="icon"
        className={cn(
          "absolute top-[35%] -translate-y-1/2 h-12 w-12 rounded-full",
          "-right-2 md:-right-8 lg:-right-14",
          "bg-primary hover:bg-primary/80 border-0 shadow-lg text-white",
          !canScrollNext && "opacity-50 cursor-not-allowed",
          arrowClassName
        )}
        onClick={() => scroll('next')}
        disabled={!canScrollNext}
        aria-label="Next slide"
      >
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  );
};
